import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/apiClient';
import { useWorkspaceStore } from './workspaceStore';
import { useLocale } from '../i18n/localeStore';
import { isSupportedUiLocale } from '../i18n/constants';
import { isI18nSwitcherEnabled } from '../i18n/gate';

// BP 56 §5 — login-sync precedence for the UI-chrome locale, run from inside
// setAuth() below (the single choke point every login/OAuth-callback/
// email-verify page already calls — see the ~10 call sites across
// LoginPage.tsx, AuthGoogleCallback.tsx, AuthMicrosoftCallback.tsx,
// AuthSamlDone.tsx, AuthVerifyDone.tsx, ChoosePlan.tsx). Imports the
// i18n/localeStore submodule directly (not the `../i18n` barrel) so this and
// `localeStore.ts`'s own reference back into authStore.ts (for the
// token/PATCH check in `setLocale`) form a safe two-file cycle — both sides
// only touch the other's binding inside a function body, never at module
// top-level, which is fine under ESM's live-binding semantics (the existing
// authStore -> workspaceStore reference below is the same pattern).
//
//   1. Server `user.locale` is set (non-null)      -> mirror down, wins.
//   2. Server null, but the local store is already `explicit`
//      (a language picked before this login, anon or a prior session) -> push
//      it up, one-shot (idempotent: safe to run on every setAuth call, not
//      just a first-ever login).
//   3. Both null -> nothing to do; the boot-time navigator negotiation in
//      i18n/index.ts already resolved a locale to show.
//
// Gated the same way `i18n/index.ts` gates its own boot negotiation: while
// `i18n_ui_enabled` is off (BP 56 §12 — ships dark), this is a no-op so a
// server-side `user.locale` can never surface a non-English UI ahead of the
// platform switch.
function syncLocaleOnLogin(user: any) {
  if (!isI18nSwitcherEnabled(user)) return;
  const localeState = useLocale.getState();
  if (isSupportedUiLocale(user?.locale)) {
    localeState.setLocale(user.locale, { explicit: true, skipPatch: true });
  } else if (localeState.explicit) {
    localeState.setLocale(localeState.locale, { explicit: true }); // no-op locale change, real PATCH
  }
}

// Module-level (not store state) — apiClient's 401 response interceptor calls
// useAuth.getState().logout() fire-and-forget on ANY 401 with a bearer token,
// including the 401 our own revoke call below will produce once the token is
// already invalid. Without this lock that re-entrant call would fire a fresh
// POST /auth/logout (with the same now-dead token) before the first call's
// `finally` ever clears state, recursing through the interceptor. Sharing one
// in-flight promise across re-entrant calls makes every nested call a no-op
// await instead of a new network request.
let logoutInFlight: Promise<void> | null = null;

interface AuthState {
  token: string | null;
  user: any | null;
  // HOME organization only — written ONCE by setAuth() at login and never
  // updated afterward. Do NOT read this as "the workspace I'm currently
  // in": switching into another workspace (via /w/:wsId — see routes.tsx)
  // only ever updates `activeOrgId` below, this field is untouched. Any
  // "current org" need (id or display name) belongs on useActiveOrgId() /
  // useActiveWorkspaceName() (frontend/src/lib/useActiveOrg.ts), not here.
  // Left named `organization` rather than renamed to `homeOrganization`:
  // ~20 call sites (routes.tsx among them, out of scope for this change)
  // already depend on this exact field name genuinely meaning "home org"
  // (login payloads, Settings' account identity card, etc.) — renaming
  // would be a large, high-risk churn for a field whose CURRENT callers are
  // now either correct-by-intent or have been moved off it.
  organization: any | null;
  // BP 03 — active workspace (org) for the session. null = home workspace
  // (user.organization_id). When set to a non-home org the apiClient sends an
  // X-Workspace-Id header so the backend scopes to that workspace.
  activeOrgId: string | null;
  setAuth: (s: { token: string; user: any; organization: any }) => void;
  setActiveOrg: (orgId: string | null) => void;
  // Finding #1 — async: it calls the server to revoke the token (see below)
  // before clearing local state. Every existing call site fires this without
  // awaiting the result, which is fine — local state still clears via the
  // `finally` on the very next microtask-turn-equivalent, so the UI signs out
  // exactly as promptly as the old synchronous version did.
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>()(persist((set, get) => ({
  token: null, user: null, organization: null, activeOrgId: null,
  // Reset activeOrgId on every fresh authentication (audit fe-rel-3). It is
  // persisted in localStorage, so without this a login that follows a prior
  // session (token expiry / a different user on the same browser without an
  // explicit Sign out) would carry the previous workspace's X-Workspace-Id into
  // the new session's first requests. A fresh auth always starts at the user's
  // home org; only a deliberate in-session switch sets activeOrgId again.
  setAuth: (s) => {
    // FE-rel-24 — a fresh auth must not inherit the previous session's persisted
    // UI context. Clear the (non-namespaced) view-state keys so a project /
    // conversation id from user A can't leak into user B's first requests.
    try {
      localStorage.removeItem('worksnip.projectTree.expanded');
      localStorage.removeItem('worksnip.chat');
    } catch { /* private-mode / SSR: ignore */ }
    // Default workspace — a fresh auth still must not inherit a PREVIOUS
    // session's activeOrgId (audit fe-rel-3, unchanged: see the field comment
    // above for why blindly carrying it over is unsafe on a shared browser).
    // But it MAY seed a workspace the server itself just told us this user
    // prefers: user.default_workspace_id, freshly read off THIS login's
    // payload — never a stale localStorage value. Only meaningful when it
    // differs from the home org (organization_id); the home org is already
    // what null resolves to via useActiveOrgId().
    const user = s.user as { organization_id?: string; default_workspace_id?: string | null } | null;
    const nextActiveOrgId =
      user?.default_workspace_id && user.default_workspace_id !== user.organization_id
        ? user.default_workspace_id
        : null;
    set({ ...s, activeOrgId: nextActiveOrgId });
    // BP 56 — UI-locale login-sync precedence (see syncLocaleOnLogin above).
    syncLocaleOnLogin(s.user);
  },
  setActiveOrg: (orgId) => set({ activeOrgId: orgId }),
  // Finding #1 — sign-out must revoke the token server-side, not just forget
  // it locally: a bare JWT has no revocation until its 7-day TTL expires, so
  // the old locals-only logout left a copied/leaked bearer token fully usable
  // for up to a week after the user thought they'd signed out. POST
  // /auth/logout bumps token_version (see authController.logout for why that
  // revokes EVERY session for this user, not just this tab).
  //
  // The call is best-effort and MUST NOT block clearing local state — a user
  // on a dead network must not be trapped signed-in in the UI just because
  // the revoke request couldn't land; the token then simply rides out its
  // normal TTL server-side, no worse than logout not existing at all. Skip
  // the call entirely when there's no token (already logged out — nothing to
  // revoke, and no Authorization header to send).
  logout: () => {
    if (logoutInFlight) return logoutInFlight; // re-entrant call — see module comment above
    logoutInFlight = (async () => {
      const hadToken = !!get().token;
      try {
        if (hadToken) await api.post('/api/v1/auth/logout');
      } catch { /* best-effort — see comment above; local state clears regardless */ }
      finally {
        set({ token: null, user: null, organization: null, activeOrgId: null });
        // The workspace-list cache (frontend/src/stores/workspaceStore.ts) is
        // authorization-adjacent and NOT persisted — but it IS kept in memory
        // for the life of the tab, so without this a sign-out followed by a
        // different account signing in on the same tab would briefly show
        // the previous account's workspace list until the next fetch lands.
        useWorkspaceStore.getState().reset();
        logoutInFlight = null;
      }
    })();
    return logoutInFlight;
  },
}), { name: 'worksnip.auth' }));

// The CURRENT workspace's org id — synchronous, no fetch required. Prefer
// this over `organization?.id` (the HOME org, see the field comment above)
// anywhere the intent is "the workspace the user is working in right now".
// activeOrgId is set by setActiveOrg()/routes.tsx when the user is inside a
// non-home workspace (/w/:wsId); null means "home", which resolves to
// user.organization_id.
export function useActiveOrgId(): string | null {
  return useAuth(s => s.activeOrgId ?? s.user?.organization_id ?? null);
}
